import type { ClientListItem } from 'src/admin/users/types';
import { useClients } from 'src/clients/hooks/useClients';
import {
  SelectPeopleModal,
  type SelectPeopleSection,
  type SelectPersonRow,
  type SelectResultRow,
} from 'src/design-system/SelectPeopleModal';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { matchesClientSearch } from 'src/utils/clientSearch';
import { formatName, NameFormat } from 'src/utils/nameFormatting';

type Props = {
  opened: boolean;
  onClose: () => void;
  selectedClientId: string | null;
  onSelect: (client: ClientListItem | null) => void;
};

const clientName = (client: { firstName: string; lastName: string }): string =>
  formatName(client.firstName, client.lastName, NameFormat.NATURAL);

export function ChooseClientModal({ opened, onClose, selectedClientId, onSelect }: Props) {
  const { clients } = useClients();
  const { flagEnabledClientSearchFullName } = useFlags();
  const selectedClient = clients.find(client => client.id === selectedClientId) ?? null;

  const rows: SelectPersonRow[] = selectedClient
    ? [
        {
          id: `client:${selectedClient.id}`,
          name: clientName(selectedClient),
          role: selectedClient.emailAddress ?? '',
          checked: true,
          onToggle: () => onSelect(null),
          ariaLabel: `${clientName(selectedClient)} selected`,
        },
      ]
    : [];

  const getResults = (search: string): SelectResultRow[] =>
    clients
      .filter(client => client.id !== selectedClient?.id)
      .filter(client =>
        matchesClientSearch(
          [client.firstName, client.lastName, client.emailAddress],
          search,
          flagEnabledClientSearchFullName,
        ),
      )
      .map(client => ({
        id: client.id,
        name: clientName(client),
        email: client.emailAddress ?? '',
        onAdd: () => {
          onSelect(client);
          onClose();
        },
      }));

  const sections: SelectPeopleSection[] = [
    {
      key: 'client',
      title: 'Clients',
      addLabel: selectedClient ? 'Change client' : 'Choose a client',
      searchPlaceholder: 'Search clients',
      noneLabel: 'No such clients',
      allowFreeText: false,
      rows,
      getResults,
    },
  ];

  return (
    <SelectPeopleModal
      opened={opened}
      onClose={onClose}
      title="Choose a client"
      confirmLabel="Done"
      sections={sections}
      defaultOpenSectionKey="client"
      onConfirm={onClose}
    />
  );
}
